import { useState, useEffect } from 'react'

export interface User {
  id: string
  email: string
  name: string
  level: number
  xp: number
  diamonds: number
  totalGames: number
  wins: number
  losses: number
  currentStreak: number
  bestStreak: number
  joinDate: string
  totalPlayTime: number // in seconds
  achievements: string[]
  ownedThemes: string[]
  ownedPowerups: string[]
  hasRemoveAds: boolean
}

export interface AnonymousProgress {
  games: number
  wins: number
  xp: number
  playTime: number
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [anonymousProgress, setAnonymousProgress] = useState<AnonymousProgress>(
    {
      games: 0,
      wins: 0,
      xp: 0,
      playTime: 0,
    },
  )
  const [showSaveProgressPrompt, setShowSaveProgressPrompt] = useState(false)

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('connectiply_user')
    const savedProgress = localStorage.getItem('connectiply_anonymous_progress')

    if (savedUser) {
      setUser(JSON.parse(savedUser))
    } else if (savedProgress) {
      setAnonymousProgress(JSON.parse(savedProgress))
    }
  }, [])

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('connectiply_user', JSON.stringify(user))
      localStorage.removeItem('connectiply_anonymous_progress')
    }
  }, [user])

  // Save anonymous progress
  useEffect(() => {
    if (!user && (anonymousProgress.games > 0 || anonymousProgress.wins > 0)) {
      localStorage.setItem(
        'connectiply_anonymous_progress',
        JSON.stringify(anonymousProgress),
      )

      // Show save progress prompt after 3 games or 1 win
      if (anonymousProgress.games >= 3 || anonymousProgress.wins >= 1) {
        setShowSaveProgressPrompt(true)
      }
    }
  }, [anonymousProgress, user])

  const signIn = (email: string, name: string) => {
    const newUser: User = {
      id: Math.random().toString(36).substring(7),
      email,
      name,
      level: 1,
      xp: anonymousProgress.xp,
      diamonds: 0,
      totalGames: anonymousProgress.games,
      wins: anonymousProgress.wins,
      losses: anonymousProgress.games - anonymousProgress.wins,
      currentStreak: 0,
      bestStreak: 0,
      joinDate: new Date().toISOString(),
      totalPlayTime: anonymousProgress.playTime,
      achievements: [],
      ownedThemes: [],
      ownedPowerups: [],
      hasRemoveAds: false,
    }
    setUser(newUser)
    setAnonymousProgress({ games: 0, wins: 0, xp: 0, playTime: 0 })
    setShowSaveProgressPrompt(false)
  }

  const signOut = () => {
    setUser(null)
    localStorage.removeItem('connectiply_user')
  }

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates }
      setUser(updatedUser)
    }
  }

  const updateAnonymousProgress = (updates: Partial<AnonymousProgress>) => {
    if (!user) {
      setAnonymousProgress((prev) => ({ ...prev, ...updates }))
    }
  }

  const addDiamonds = (amount: number) => {
    if (user) {
      updateUser({ diamonds: user.diamonds + amount })
    }
  }

  const spendDiamonds = (amount: number): boolean => {
    if (user && user.diamonds >= amount) {
      updateUser({ diamonds: user.diamonds - amount })
      return true
    }
    return false
  }

  return {
    user,
    anonymousProgress,
    showSaveProgressPrompt,
    setShowSaveProgressPrompt,
    signIn,
    signOut,
    updateUser,
    updateAnonymousProgress,
    addDiamonds,
    spendDiamonds,
    isAuthenticated: !!user,
  }
}
